import { effectRegistry, type AlterlyEffectId, type AlterlyEffectInstance } from "../../core/effects/effectRegistry";

type EffectsPanelProps = {
  effects: AlterlyEffectInstance[];
  selectedId: string | null;
  canUndo: boolean;
  canRedo: boolean;
  onAdd: (definitionId: AlterlyEffectId) => void;
  onSelect: (instanceId: string) => void;
  onToggle: (instanceId: string) => void;
  onDuplicate: (instanceId: string) => void;
  onRemove: (instanceId: string) => void;
  onMove: (instanceId: string, direction: -1 | 1) => void;
  onUndo: () => void;
  onRedo: () => void;
};

const availableEffects: readonly AlterlyEffectId[] = ["halftone", "noise"];

export function EffectsPanel({
  effects,
  selectedId,
  canUndo,
  canRedo,
  onAdd,
  onSelect,
  onToggle,
  onDuplicate,
  onRemove,
  onMove,
  onUndo,
  onRedo,
}: EffectsPanelProps) {
  return (
    <div className="effect-stack">
      <div className="effect-stack__head">
        <div className="effect-stack__title-row">
          <div>
            <div className="panel-label">Effects</div>
            <strong>Build the look</strong>
          </div>
          <div className="effect-stack__history" aria-label="Effect history">
            <button type="button" disabled={!canUndo} onClick={onUndo} aria-label="Undo last effect change">Undo</button>
            <button type="button" disabled={!canRedo} onClick={onRedo} aria-label="Redo effect change">Redo</button>
          </div>
        </div>

        <div className="effect-library" aria-label="Effect library">
          <div className="effect-library__label">Library</div>
          <div className="effect-library__grid">
            {availableEffects.map((definitionId) => {
              const definition = effectRegistry[definitionId];
              return (
                <button
                  key={definitionId}
                  type="button"
                  className="effect-library__item"
                  onClick={() => onAdd(definitionId)}
                >
                  <span className={`effect-library__swatch effect-library__swatch--${definitionId}`} aria-hidden="true" />
                  <span>
                    <strong>{definition.name}</strong>
                    <small>{definition.category}</small>
                  </span>
                  <b aria-hidden="true">+</b>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="effect-stack__section-label">
        <span>Stack</span>
        <span>{effects.length}</span>
      </div>

      <div className="effect-stack__list">
        {effects.length === 0 && <div className="panel-placeholder">Add an effect from the library.</div>}
        {effects.map((effect, index) => {
          const definition = effectRegistry[effect.definitionId];
          const selected = selectedId === effect.instanceId;
          return (
            <div key={effect.instanceId} className={`effect-stack__item${selected ? " is-selected" : ""}`}>
              <button type="button" className="effect-stack__select" onClick={() => onSelect(effect.instanceId)}>
                <span>
                  <strong>{definition.name}</strong>
                  <small>{definition.category}</small>
                </span>
              </button>

              <button
                type="button"
                className={`effect-stack__status${effect.enabled ? " is-enabled" : ""}`}
                onClick={() => onToggle(effect.instanceId)}
                aria-pressed={effect.enabled}
              >
                {effect.enabled ? "On" : "Off"}
              </button>

              <div className="effect-stack__actions" aria-label={`${definition.name} actions`}>
                <button type="button" disabled={index === 0} onClick={() => onMove(effect.instanceId, -1)} aria-label="Move effect up">↑</button>
                <button type="button" disabled={index === effects.length - 1} onClick={() => onMove(effect.instanceId, 1)} aria-label="Move effect down">↓</button>
                <button type="button" onClick={() => onDuplicate(effect.instanceId)} aria-label="Duplicate effect">Duplicate</button>
                <button type="button" onClick={() => onRemove(effect.instanceId)} aria-label="Remove effect">Remove</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
